/**
 * Modulo Commesse: schemi e regole pure (tariffe, tetto spese, quote, margine).
 */
import { z } from "zod";
import { isoDay, optionalText, uuid } from "./common.js";

export const CONTRACT_STATUSES = ["draft", "pending", "active", "closed", "canceled"] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];
export const CONTRACT_STATUS_LABEL: Record<ContractStatus, string> = { draft: "Bozza", pending: "In approvazione", active: "Attiva", closed: "Chiusa", canceled: "Annullata" };
export const BILLING_MODES = ["time_material", "plan"] as const;
export const BILLING_MODE_LABEL: Record<(typeof BILLING_MODES)[number], string> = { time_material: "A consuntivo", plan: "Piano di fatturazione" };
export const RATE_MODES = ["level", "flat"] as const;
export const RATE_UNITS = ["hour", "day"] as const;
export const EXPENSE_POLICIES = ["rebill", "included"] as const;
export const EXPENSE_POLICY_LABEL: Record<(typeof EXPENSE_POLICIES)[number], string> = { rebill: "Rifatturate al cliente", included: "Comprese (a carico della commessa)" };
export const COMMISSION_BASES = ["revenue", "margin", "collected"] as const;
export type CommissionBasis = (typeof COMMISSION_BASES)[number];
export const COMMISSION_BASIS_LABEL: Record<CommissionBasis, string> = { revenue: "del fatturato", margin: "del margine", collected: "dell'incassato" };

export const contractInputSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    companyId: uuid.nullable(),
    dealId: uuid.nullish(),
    ownerId: uuid.nullable(),
    status: z.enum(CONTRACT_STATUSES).default("draft"),
    probabilityPercent: z.number().int().min(0).max(100).default(50),
    customerRef: optionalText(120),
    startDate: isoDay.nullable(),
    endDate: isoDay.nullable(),
    autoRenew: z.boolean().default(false),
    noticeDays: z.number().int().min(0).max(730).default(0),
    valueCents: z.number().int().min(0).max(100_000_000_000),
    billingMode: z.enum(BILLING_MODES),
    rateMode: z.enum(RATE_MODES),
    flatRateCents: z.number().int().min(0).max(100_000_000).default(0),
    rateUnit: z.enum(RATE_UNITS),
    hoursPerDay: z.number().min(1).max(24).default(8),
    expensePolicy: z.enum(EXPENSE_POLICIES),
    expenseCapPercent: z.number().min(0).max(100).nullable(),
    vatRate: z.number().min(0).max(100).default(22),
    paymentTermsDays: z.number().int().min(0).max(365).nullable(),
    notes: optionalText(5000),
    rates: z.array(z.object({ levelId: uuid, rateCents: z.number().int().min(0).max(100_000_000) })).max(50).default([]),
    projectIds: z.array(uuid).max(100).default([]),
  })
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, { message: "La fine precede l'inizio", path: ["endDate"] });
export type ContractInput = z.infer<typeof contractInputSchema>;

export const billingItemSchema = z.object({ id: uuid.nullish(), label: z.string().trim().min(1).max(200), dueDate: isoDay, amountCents: z.number().int().min(0).max(100_000_000_000) });
export const billingPlanSchema = z.object({ contractId: uuid, items: z.array(billingItemSchema).max(120) });
/** Genera un piano a rate costanti (mensili/trimestrali) tra due date. */
export const billingPlanGenerateSchema = z.object({ contractId: uuid, every: z.enum(["month", "quarter"]), from: isoDay, count: z.number().int().min(1).max(60), totalCents: z.number().int().min(1) });

export const contractRoleSchema = z
  .object({
    id: uuid.nullish(),
    contractId: uuid,
    label: z.string().trim().min(1).max(80),
    userId: uuid.nullable(),
    payeeName: optionalText(120),
    basis: z.enum(COMMISSION_BASES),
    percent: z.number().min(0).max(100),
  })
  .refine((v) => !!v.userId || !!v.payeeName, { message: "Indica la persona o il nominativo", path: ["payeeName"] });
export const payoutSchema = z.object({ roleId: uuid, amountCents: z.number().int().min(1), paidAt: isoDay, note: optionalText(300) });

export const contractInvoiceSchema = z.object({
  contractId: uuid,
  /** fino a questa data (ore e rate) */
  to: isoDay,
  include: z.object({ fees: z.boolean().default(true), plan: z.boolean().default(true), expenses: z.boolean().default(true) }).default({ fees: true, plan: true, expenses: true }),
});

export const formatContractCode = (year: number, number: number) => `CO-${year}-${String(number).padStart(4, "0")}`;

// ── regole pure ───────────────────────────────────────────────────────

/** Quantità e prezzo di una riga di onorari: minuti → ore o giorni. */
export function feeQuantity(minutes: number, unit: "hour" | "day" | string, hoursPerDay: number) {
  const h = minutes / 60;
  return Math.round((unit === "day" ? h / (hoursPerDay || 8) : h) * 100) / 100;
}

/**
 * Tetto delle spese rifatturabili: percentuale del valore di commessa (o, se
 * il valore manca, degli onorari fatturati compresi quelli di questa fattura),
 * meno quanto già rifatturato. null = nessun tetto.
 */
export function expenseAllowance(opts: { capPercent: number | null; valueCents: number; feesBilledCents: number; expensesBilledCents: number }) {
  if (opts.capPercent == null) return null;
  const base = opts.valueCents > 0 ? opts.valueCents : opts.feesBilledCents;
  return Math.max(0, Math.round((base * opts.capPercent) / 100) - opts.expensesBilledCents);
}

/** Quota maturata di un ruolo: percentuale della base scelta (il margine negativo non matura). */
export function commissionAccrued(basis: CommissionBasis | string, percent: number, e: { revenueCents: number; marginCents: number; collectedCents: number }) {
  const base = basis === "margin" ? Math.max(0, e.marginCents) : basis === "collected" ? e.collectedCents : e.revenueCents;
  return Math.round((base * percent) / 100);
}

export interface ContractEconomics {
  revenueCents: number;
  collectedCents: number;
  personnelCents: number;
  expensesCents: number;
  payablesCents: number;
  commissionsCents: number;
}
/** Margine: ricavi fatturati meno personale, spese e fornitori (prima e dopo le quote). */
export function contractMargin(e: Omit<ContractEconomics, "commissionsCents">) {
  const costs = e.personnelCents + e.expensesCents + e.payablesCents;
  const margin = e.revenueCents - costs;
  return { costsCents: costs, marginCents: margin, marginPercent: e.revenueCents > 0 ? Math.round((margin / e.revenueCents) * 1000) / 10 : null };
}
